"use client";

import React from 'react';
import Image from 'next/image';
// import { assets } from '@/app/lib/assets/assets'; // Remove assets import

// Define FeatureCardProps with large image path
interface FeatureCardProps {
  title: string;
  description: string;
  // icon: string;
  image: string; // Use large image path
}

// Modify FeatureCard to use large image
const FeatureCard: React.FC<FeatureCardProps> = ({ title, description, image }) => {
  return (
    <div className="mb-16 md:mb-24 transform transition-all duration-500 hover:translate-x-2">
      <div className="bg-[#121212]/80 rounded-xl border border-white/20 hover:border-white/40 shadow-[0_0_8px_rgba(255,255,255,0.15)] transition-all duration-300 flex flex-col md:flex-row items-center p-6 md:p-8 overflow-hidden">
        {/* Text on the left */}
        <div className="w-full md:w-2/5 md:pr-4 mb-10 md:mb-0">
          <h3 className="text-xl md:text-2xl font-semibold mb-4 text-gray-100">{title}</h3>
          <p className="text-base text-gray-300">{description}</p>
        </div>
        
        {/* Image on the right - completely revised for mobile */}
        <div className="w-full md:w-3/5 md:pl-4">
          <div className="relative w-full h-40 md:h-64 mx-auto">
            <Image
              src={image}
              alt={title}
              width={500}
              height={300}
              className="mx-auto h-auto w-auto max-h-full"
              style={{ maxWidth: '100%' }}
              priority
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const AdditionalFeaturesSection = () => {
  // Restore data with large image paths
  const additionalFeatures = [
    {
      title: "Search any word across your meetings",
      description: "Find the meeting and jump to the timestamp exactly where the word was talked about",
      image: '/images/home/search_words_across_meetings.svg',
      // icon: assets.advancedFeatures.search, 
    },
    {
      title: "Efficient Task Management",
      description: "Convert action items into to-dos in the task management tool of your choice​",
      image: '/images/home/efficient_task_management.svg',
      // icon: assets.advancedFeatures.taskManagement,
    },
    {
      title: "Instantly Share Key Meeting Notes",
      description: "Easily share comprehensive meeting summaries, promoting collaboration and informed decision-making with your team and clients.",
      image: '/images/home/share_notes.svg',
      // icon: assets.advancedFeatures.sharing,
    },
    {
      title: "We're GDPR Compliant",
      description: "Our advanced encryption ensures confidentiality, empowering you to meet confidently with secure transmissions and encrypted storage.",
      image: '/images/home/gdpr_compliant.png',
      // icon: assets.advancedFeatures.security,
    },
  ];

  return (
    <div className="bg-[#121212] text-white py-16 md:py-24 pb-1.5 md:pb-2.5 relative overflow-visible">
      <div className="container mx-auto px-4 w-full md:w-[80%] overflow-visible">
        <div className="md:flex relative overflow-visible">
          {/* Fixed title section on the left with sticky behavior for all features */}
          <div className="md:w-[40%] md:sticky md:top-24 md:h-min md:self-start mb-12 md:mb-0">
            <div className="md:pr-8">
              <div className="flex flex-col mb-6 md:mb-4">
                <h2 className="text-3xl md:text-5xl font-bold text-gray-100">
                  <div className="inline-flex flex-wrap items-center justify-center gap-3 md:flex-col md:items-start md:justify-start md:gap-x-0 md:gap-y-2">
                    <span>Connect</span>
                    <span>Collaborate</span>
                    <span>Conquer</span>
                  </div>
                </h2>
              </div>
              <p className="text-gray-300 md:text-lg mb-12 md:mb-6">
                MeetMinutes is packed with features that streamline your meetings, turning them into efficient collaborative sessions that save your time.
              </p>
            </div>
          </div>
          
          {/* All 4 features - will scroll alongside the sticky title */}
          <div className="md:w-[55%] md:ml-auto overflow-visible">
            {additionalFeatures.map((feature) => (
              <FeatureCard
                key={feature.title} 
                title={feature.title}
                description={feature.description}
                image={feature.image}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdditionalFeaturesSection; 